import {
    ApolloClient,
    ApolloLink,
    createHttpLink,
    InMemoryCache,
    split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';

import { refreshIdToken } from './auth-refresh';
import { shouldUseCredentialedGraphQL } from './helpers/credentialedGraphql';

import { getValidIdToken } from './cookie-auth';

const getAuthorizationHeader = async () => {
    const token = await getValidIdToken(refreshIdToken);
    return token ? `Bearer ${token}` : 'null';
};

const authLink = setContext(async (_, previousContext) => {
    const { headers } = previousContext;

    return {
        headers: {
            ...headers,
            authorization: await getAuthorizationHeader(),
        },
    };
});

const httpLinkOptions = {
    uri: __GRAPHQL_API__,
};

if (shouldUseCredentialedGraphQL()) {
    httpLinkOptions.credentials = 'include';
}

const httpLink = createHttpLink(httpLinkOptions);
const wsLink = new WebSocketLink({
    uri: __GRAPHQL_WS_API__,
    options: {
        lazy: true,
        reconnect: true,
    },
});

wsLink.subscriptionClient.use([
    {
        applyMiddleware: async (options, next) => {
            options.extensions = {
                authorization: await getAuthorizationHeader(),
            };
            next();
        },
    },
]);

async function refreshSubscriptionAuthorization() {
    const authorization = await getAuthorizationHeader();

    Object.values(this.operations ?? {}).forEach((operation) => {
        // eslint-disable-next-line no-param-reassign
        operation.options.extensions = {
            authorization,
        };
    });
    (this.unsentMessagesQueue ?? []).forEach((message) => {
        if (message.type === 'start') {
            // eslint-disable-next-line no-param-reassign
            message.payload.extensions = {
                authorization,
            };
        }
    });
}

wsLink.subscriptionClient.connectionParams =
    refreshSubscriptionAuthorization.bind(wsLink.subscriptionClient);

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
        );
    },
    wsLink,
    httpLink,
);

export const apolloClient = new ApolloClient({
    link: ApolloLink.from([authLink, splitLink]),
    cache: new InMemoryCache(),
});
